
import { useState, useEffect, useMemo } from 'react';
import type { UsageScenario } from '../../types';

interface UseScenarioStepProps {
    scenarios: UsageScenario[];
    onSelect: (selectedIds: string[]) => void;
    keywordsBySite?: Record<string, string>;
}

export const useScenarioStep = ({
    scenarios,
    onSelect,
    keywordsBySite = {}
}: UseScenarioStepProps) => {
    // 获取选中的场景ID
    const selectedIds = useMemo(() =>
        scenarios.filter(s => s.selected).map(s => s.id),
        [scenarios]
    );

    // 目标站点选择状态 - 默认选中有关键词的站点
    const getDefaultSites = () => {
        const sitesWithKeywords = Object.entries(keywordsBySite)
            .filter(([_, kw]) => kw && kw.trim())
            .map(([site]) => site);
        return sitesWithKeywords.length > 0 ? sitesWithKeywords : ['US'];
    };

    const [targetSites, setTargetSites] = useState<string[]>(getDefaultSites());

    // 当 keywordsBySite 变化时更新默认选中
    useEffect(() => {
        const sitesWithKeywords = Object.entries(keywordsBySite)
            .filter(([_, kw]) => kw && kw.trim())
            .map(([site]) => site);
        if (sitesWithKeywords.length > 0) {
            setTargetSites(sitesWithKeywords);
        }
    }, [keywordsBySite]);

    // 切换选择
    const handleToggle = (id: string) => {
        const newSelected = selectedIds.includes(id)
            ? selectedIds.filter(i => i !== id)
            : [...selectedIds, id];
        onSelect(newSelected);
    };

    // 全选/取消全选
    const handleSelectAll = () => {
        if (selectedIds.length === scenarios.length) {
            onSelect([]);
        } else {
            onSelect(scenarios.map(s => s.id));
        }
    };

    // 快速选择（前5个）
    const handleQuickSelect = () => {
        onSelect(scenarios.slice(0, 5).map(s => s.id));
    };

    return {
        selectedIds,
        targetSites,
        setTargetSites,
        handleToggle,
        handleSelectAll,
        handleQuickSelect
    };
};
